import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import CompanyInterestSelection from './CompanyInterestSelection';
import StudentAccountCreation from './StudentAccountCreation';
import StudentLogin from './StudentLogin';
import { API_URL } from './utils/authUtils';
import { devLogSafe, devLog } from './utils/secureLogger';

const BACKEND_URL = API_URL;

const UpdateApplicationWrapper = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const magicLink = searchParams.get('ml');
  const [authStatus, setAuthStatus] = useState('checking');
  const [applicantInfo, setApplicantInfo] = useState(null);
  const [error, setError] = useState(null);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // Only check once to prevent double-validation in React Strict Mode
    if (!hasCheckedRef.current) {
      hasCheckedRef.current = true;
      checkAuthStatus();
    }
  }, [token, magicLink]);

  const checkAuthStatus = async () => {
    if (!token && !magicLink) {
      setError('No token provided');
      setAuthStatus('error');
      return;
    }

    try {
      let response;

      // Check if using magic link (ml parameter) or regular token
      if (magicLink) {
        // Validate magic link
        response = await axios.get(`${BACKEND_URL}/api/validate-magic-link?ml=${magicLink}`);
      } else {
        // Validate regular update token
        response = await axios.get(`${BACKEND_URL}/api/validate-update-token?token=${token}`);
      }

      const data = response.data;
      
      devLogSafe('Token validation response', data);
      
      setApplicantInfo({
        id: data.applicant_id,
        name: data.name,
        email: data.email || data.university_email || ''  // Fallback in case field name varies
      });

      // Check if already authenticated via JWT token
      const authToken = localStorage.getItem('studentAuthToken');
      
      // Determine what action is needed
      if (!data.has_password) {
        devLog('Student needs to create account');
        setAuthStatus('needs-creation');
      } else if (!authToken) {
        devLog('Student needs to login');
        setAuthStatus('needs-login');
      } else {
        devLog('Student is authenticated');
        setAuthStatus('authenticated');
      }
    } catch (err) {
      console.error('Token validation error:', err);
      setError(err.response?.data?.detail || 'Invalid or expired token');
      setAuthStatus('error');
    }
  };

  const handleAuthSuccess = () => {
    devLog('Authentication successful, proceeding to CompanyInterestSelection');
    setAuthStatus('authenticated');
  };

  if (authStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (authStatus === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Error</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  switch (authStatus) {
    case 'needs-creation':
      return <StudentAccountCreation
        applicantInfo={applicantInfo}
        token={token || magicLink}
        isMagicLink={!!magicLink}
        onSuccess={handleAuthSuccess}
      />;
    case 'needs-login':
      return <StudentLogin
        applicantInfo={applicantInfo}
        token={token || magicLink}
        isMagicLink={!!magicLink}
        onSuccess={handleAuthSuccess}
      />;
    case 'authenticated':
      return <CompanyInterestSelection />;
    default:
      return null;
  }
};

export default UpdateApplicationWrapper;